import { Component } from 'preact';
import ArenaPackOpening from './ArenaPackOpening';
import { resolvePrinting } from '../utils/sorcery/foil';
import { t } from '../localization/t';

const SET_LABELS = { beta: 'Beta', arthurian: 'Arthurian Legends', gothic: 'Gothic' };

/**
 * Sealed pack-opening ceremony. Wraps ArenaPackOpening. The exact same
 * booster-opening UI + animations + particle FX the store uses. And
 * walks the user through all 6 of their sealed packs in order.
 *
 * Server contract:
 *   props.draftedCards: flat array of 90 picks from draft_picks
 *     ({ cardId, printingId, cardName, rarity, foiling }).
 *     First 15 = pack 1, next 15 = pack 2, etc.
 *   props.sorceryCards: normalised Sorcery DB for card/printing
 *     lookup. ArenaPackOpening expects { card, printing, rarity,
 *     printing.foiling } per entry.
 *   props.setKey: the booster set. Themes the pack art.
 *   props.onComplete: fires once the user opens the final pack,
 *     routes them into DraftDeckBuilder.
 */
export default class SealedPackOpener extends Component {
  constructor(props) {
    super(props);
    this.state = {
      packIndex: 0,
      // Forcing ArenaPackOpening to remount per pack via a key;
      // without this the internal phase stays on 'summary' and the
      // opening animation won't replay when we advance.
      iteration: 0,
    };
  }

  // Slice the flat pick array into 6 packs of 15 cards. Enrich each
  // pick with its full card + printing from sorceryCards so
  // ArenaPackOpening's existing render path works unchanged. Cached
  // on the instance so enrichment runs once per mount.
  packs() {
    if (this._cachedPacks) return this._cachedPacks;
    const picks = this.props.draftedCards || [];
    const cards = this.props.sorceryCards || [];
    const cardById = new Map(cards.map((c) => [c.unique_id, c]));
    const PACK_SIZE = 15;
    const setKey = this.props.setKey || 'beta';
    const setLabel = SET_LABELS[setKey] || setKey;

    const enriched = picks.map((pick) => {
      const card = cardById.get(pick.cardId) || {
        unique_id: pick.cardId,
        name: pick.cardName || 'Unknown',
        printings: [],
      };
      const printing = resolvePrinting(card, pick.printingId, pick.foiling || 'S')
        || { foiling: pick.foiling || 'S', image_url: null };
      return {
        card,
        printing,
        rarity: pick.rarity || card.rarity || 'Ordinary',
      };
    });

    const packs = [];
    for (let i = 0; i < enriched.length; i += PACK_SIZE) {
      packs.push({
        setKey,
        setLabel,
        cards: enriched.slice(i, i + PACK_SIZE),
      });
    }
    this._cachedPacks = packs;
    return packs;
  }

  handleOpenAnother = () => {
    const packs = this.packs();
    const next = this.state.packIndex + 1;
    if (next >= packs.length) {
      this.props.onComplete?.();
      return;
    }
    this.setState((s) => ({ packIndex: next, iteration: s.iteration + 1 }));
  };

  handleDone = () => {
    // Sealed doesn't have a "back to store" destination mid-ceremony;
    // a user who wants out cancels the event from the hub. Treat
    // the ArenaPackOpening BackButton as "skip rest." Jump straight
    // to the deck builder with the full pool already granted.
    this.props.onComplete?.();
  };

  render() {
    const packs = this.packs();
    if (packs.length === 0) {
      return (
        <div className="fixed inset-0 z-50 bg-black flex items-center justify-center" style={{ color: '#c5b98e' }}>
          {t('Loading pool…')}
        </div>
      );
    }
    const { packIndex, iteration } = this.state;
    const currentPack = packs[packIndex];
    const remaining = packs.length - packIndex - 1;
    const isLastPack = remaining === 0;

    return (
      <ArenaPackOpening
        key={`sealed-pack-${iteration}`}
        pack={currentPack}
        autoOpen
        profile={this.props.profile}
        allPendingPacks={[]}
        remainingPacks={remaining}
        canAffordAnother={false}
        // On the final pack swap the Back button for an explicit
        // "Continue to Deck Builder" CTA. Sealed has no meaningful
        // "back" destination mid-ceremony and the user needs a
        // clear forward affordance to advance into the builder.
        continueLabel={isLastPack ? t('Continue to Deck Builder') : undefined}
        backTitle={t('Back to Hub')}
        onDone={this.handleDone}
        onOpenAnother={this.handleOpenAnother}
      />
    );
  }
}
